import i18n from 'i18next';
import qs from 'qs';
import {
  customUrlEncode,
  prepareFilters,
  preparePagination,
  prepareSorters,
} from '@utils/apiParamsUtils';
import { axiosClient } from '@/libs/axios-client';

/**
 * Extracts the filename from the Content-Disposition header.
 */
const extractFileNameFromDisposition = (disposition) => {
  if (!disposition) return null;
  const match = disposition.match(/filename="(.+?)"/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

/**
 * Generates a fallback file name based on content type and timestamp.
 */
const generateFallbackFileName = (contentType) => {
  const ext = contentType.includes('pdf') ? 'pdf' : 'docx';
  return `document-${Date.now()}.${ext}`;
};

/**
 * Saves a Blob as a file using a temporary anchor element.
 */
const saveBlobAsFile = (data, type, fileName) => {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Download a document by posting a payload and saving the blob response as a file.
 * @param {Object} payload - Payload containing document info.
 * @returns {Promise<Object>} Message about the success of the download.
 */
export const downloadDocument = async (payload) => {
  try {
    const response = await axiosClient.post(`/documents/download`, payload, {
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'];
    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);

    saveBlobAsFile(response.data, contentType, fileName);

    return { message: 'Document downloaded successfully.' };
  } catch (error) {
    console.error('Failed to download document:', error);
    throw error;
  }
};

/**
 * Renders a document as PDF and opens it inline in a new browser tab (preview),
 * instead of forcing a download. Works for any document type the download
 * endpoint can render (request, custom, contract, additional holiday days...).
 * @param {string} documentId
 * @returns {Promise<Object>} Message about the success of the preview.
 */
export const viewDocument = async (documentId) => {
  // The tab is opened on the click itself: one opened after the request is a
  // popup the browser blocks. It used to pass `noopener`, which makes
  // window.open return null, so the blob was revoked before the tab read it.
  const win = window.open('', '_blank');
  if (win) {
    win.opener = null;
    // A document edited in the browser goes through a DOCX → PDF conversion
    // that takes several seconds; a bare white tab for that long reads as
    // broken, so the tab says it is loading until the PDF replaces it.
    const message = i18n.t('viewPdfLoading', {
      ns: 'menus',
      defaultValue: 'Učitavanje dokumenta…',
    });
    win.document.title = message;
    win.document.body.style.cssText =
      'margin:0;height:100vh;display:flex;align-items:center;justify-content:center;font:16px system-ui,sans-serif;color:#52525b';
    win.document.body.textContent = message;
  }

  try {
    const response = await axiosClient.post(
      `/documents/download`,
      { documentId, type: 'pdf', returnDoc: true },
      { responseType: 'blob' }
    );

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    if (win) {
      win.location.href = url;
    } else {
      // Popups blocked outright: save the file instead of doing nothing.
      const link = document.createElement('a');
      link.href = url;
      link.download = `document_${documentId}.pdf`;
      link.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);

    return { message: 'Document opened.' };
  } catch (error) {
    win?.close();
    console.error('Failed to view document:', error);
    throw error;
  }
};

// Filters and sort of the HR requests list, shared by the list and its Excel
// export so the file always matches what the table shows.
const buildDocumentsQuery = (params, from) => {
  const filters = prepareFilters(params, {
    email: 'default',
    company: 'default',
    month: 'default',
    year: 'default',
    firstName: 'default',
    lastName: 'default',
    personalNo: 'default',
    employee: 'default',
    type: 'default',
    createdAt: 'range',
    updatedAt: 'range',
    status: 'uppercaseArray',
    numOfDays: 'default',
    datesFromTo: 'datesFromTo',
    typeOfLeave: 'array',
  });

  if (from === 'employee-hr-documents') {
    delete filters.type;
  }

  const sorters = prepareSorters(params, {
    firstName: 'sortFirstName',
    lastName: 'sortLastName',
    status: 'sortStatus',
    createdAt: 'sortCreatedAt',
    typeOfLeave: 'sortTypeOfLeave',
  });

  return { filters, sorters };
};

const serializeDocumentsParams = (params) => {
  if (params.filter) {
    params.filter = JSON.stringify(params.filter);
  }

  if (params.sorter) {
    params.sorter = JSON.stringify(params.sorter);
  }

  return qs.stringify(params, {
    encode: true,
    arrayFormat: 'brackets',
    encoder: customUrlEncode,
  });
};

/**
 * Retrieves all requests.
 * @returns {Promise<Object[]>} A promise that resolves with an array of request objects.
 * @throws Will throw an error if the request fails.
 */
export const retrieveAllDocuments = async ({ params, from }) => {
  const { filters, sorters } = buildDocumentsQuery(params, from);
  const pagination = preparePagination(params);

  try {
    const response = await axiosClient.get('/documents', {
      params: {
        filter: filters,
        sorter: sorters,
        ...pagination,
      },
      paramsSerializer: serializeDocumentsParams,
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve documents:', error);
    throw error;
  }
};

/**
 * Downloads the HR requests list as Excel: every row matching the list's
 * current filters and sort, not only the page on screen.
 * @param {Object} params - The list's query params.
 * @returns {Promise<void>}
 */
export const exportHrRequestsExcel = async ({ params, from }) => {
  const { filters, sorters } = buildDocumentsQuery(params, from);

  const response = await axiosClient.get('/documents/export', {
    params: { filter: filters, sorter: sorters },
    paramsSerializer: serializeDocumentsParams,
    responseType: 'blob',
  });

  const contentType = response.headers['content-type'];
  const fileName =
    extractFileNameFromDisposition(response.headers['content-disposition']) ||
    `HR_requests_${Date.now()}.xlsx`;

  saveBlobAsFile(response.data, contentType, fileName);
};

/**
 * Deletes a document by their ID.
 * @param {string} documentId - The unique identifier of the document.
 * @returns {Promise<Object>} A promise that resolves when the document is deleted.
 * @throws Will throw an error if the delete request fails.
 */
export const deleteDocumentById = async (documentId, params = {}) => {
  try {
    // params: { overrideLawWarning, lawWarningReason } after a first-part warning.
    const response = await axiosClient.delete(`/documents/${documentId}`, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to delete document with ID ${documentId}:`, error);
    throw error;
  }
};

const DocumentsService = {
  downloadDocument,
  viewDocument,
  retrieveAllDocuments,
  exportHrRequestsExcel,
  deleteDocumentById,
};

export default DocumentsService;
